import AnalysisIntersectIcon from 'assets/icons/analysis/analysis-intersec.svg?react'
import AnalysisBufferIcon from 'assets/icons/analysis/analysis-buffer.svg?react'
import AnalysisAddColumnIcon from 'assets/icons/analysis/analysis-add-column.svg?react'
import AnalysisColumnValueIcon from 'assets/icons/analysis/analysis-column-value.svg?react'
import AnalysisClusteringKMeansIcon from 'assets/icons/analysis/analysis-clustering-k-means.svg?react'
import AnalysisCentroidIcon from 'assets/icons/analysis/analysis-centroid.svg?react'
import AnalysisTradeAreasIcon from 'assets/icons/analysis/analysis-isolines.svg?react'
import { FormattedMessage } from 'react-intl'
import { ReactElement, useEffect } from 'react'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { useAppDispatch, useAppSelector } from 'app/state'
import { useMemo, useState } from 'react'
import IntersectForm from './IntersectAndAggregate/IntersectForm'
import ColumnsFromSourceForm from './ColumnsFromSource/ColumnsFromSourceForm'
import { generateCreateTableFromQuery } from './CreateTable/queries'
import { ProviderType } from 'features/connections'
import BufferForm from './Buffer/BufferForm'
import FilterByColumnForm from './FilterByColumn/FilterByColumnForm'
import { setSecondSourceTable, setQuerySource } from 'features/analysis/state/analysisSlice'
import ClusteringKMeansForm from './ClusteringKMeans/ClusteringKMeansForm'
import CentroidForm from './Centroid/CentroidForm'
import TradeAreasForm from './TradeAreas/TradeAreasForm'
import { generateLaunchTradeAreasQuery } from './TradeAreas/queries'
import { DEFAULT_GEOM_COLUMN } from 'features/builder/ui/builderConstants'

export interface CatalogItem {
  id: AnalysisType
  icon: ReactElement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: (props?: any) => ReactElement
  label: string | ReactElement
  description: string | ReactElement
  optionalGeom?: boolean
  url?: string
  disabled?: boolean
  analyticsToolbox?: boolean | ProviderType[]
  analyticsToolboxVersions?: { [K in ProviderType]?: string }
  unsupported?: ProviderType[]
  onlyAsTable?: boolean
}

export const Catalog: CatalogItem[] = [
  {
    id: AnalysisType.Intersect,
    icon: <AnalysisIntersectIcon />,
    form: IntersectForm,
    label: <FormattedMessage id="builder.analysis.catalog.intersect.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.intersect.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST],
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#intersect-and-aggregate'
  },
  {
    id: AnalysisType.AddColumn,
    icon: <AnalysisAddColumnIcon />,
    form: ColumnsFromSourceForm,
    label: <FormattedMessage id="builder.analysis.catalog.addColumn.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.addColumn.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST, ProviderType.CARTO],
    optionalGeom: true,
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#add-column-from-second-source'
  },
  {
    id: AnalysisType.ColumnValue,
    icon: <AnalysisColumnValueIcon />,
    form: FilterByColumnForm,
    label: <FormattedMessage id="builder.analysis.catalog.columnValue.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.columnValue.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST],
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#filter-by-column-value'
  },
  {
    id: AnalysisType.Buffer,
    icon: <AnalysisBufferIcon />,
    form: BufferForm,
    label: <FormattedMessage id="builder.analysis.catalog.buffer.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.buffer.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST],
    analyticsToolbox: [ProviderType.SNOWFLAKE],
    analyticsToolboxVersions: {
      [ProviderType.SNOWFLAKE]: '2022.04.07'
    },
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#create-buffers'
  },
  {
    id: AnalysisType.ClusteringKMeans,
    icon: <AnalysisClusteringKMeansIcon />,
    form: ClusteringKMeansForm,
    label: <FormattedMessage id="builder.analysis.catalog.clusteringKMeans.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.clusteringKMeans.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST],
    analyticsToolbox: [ProviderType.BIGQUERY, ProviderType.SNOWFLAKE, ProviderType.REDSHIFT],
    analyticsToolboxVersions: {
      [ProviderType.BIGQUERY]: '2021.05.06',
      [ProviderType.SNOWFLAKE]: '2021.05.24',
      [ProviderType.REDSHIFT]: '2021.10.21'
    },
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#k-means-clustering'
  },
  {
    id: AnalysisType.Centroid,
    icon: <AnalysisCentroidIcon />,
    form: CentroidForm,
    label: <FormattedMessage id="builder.analysis.catalog.centroid.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.centroid.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST],
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#compute-centroids'
  },
  {
    id: AnalysisType.TradeAreas,
    icon: <AnalysisTradeAreasIcon />,
    form: TradeAreasForm,
    label: <FormattedMessage id="builder.analysis.catalog.tradeAreas.title" />,
    description: <FormattedMessage id="builder.analysis.catalog.tradeAreas.description" />,
    unsupported: [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST, ProviderType.POSTGRES],
    analyticsToolbox: true,
    analyticsToolboxVersions: {
      [ProviderType.SNOWFLAKE]: '2022.06.09',
      [ProviderType.REDSHIFT]: '2022.06.07'
    },
    url: 'https://docs.carto.com/carto-user-manual/maps/sql-analyses/#trade-areas',
    onlyAsTable: true
  }
]

export function useSelectedAnalysisForm() {
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId || '')
  const selectedAnalysis = useAppSelector((state) => state.analysis.selectedAnalysis)
  const settingsSnapshot = useAppSelector((state) => state.analysis.settingsSnapshot)
  const [settings, setSettings] = useState<AnalysisSettings>()
  const [query, setQuery] = useState<string | undefined>()
  const dispatch = useAppDispatch()
  const geoColumn = useAppSelector(
    (state) => state.keplerGl.map.visState.datasets[sourceDatasetId]?.geoColumn || DEFAULT_GEOM_COLUMN
  )

  const { shouldInitWithPreviousSettings, choosenAnalysisSettings, selectedAnalysisItem } = useMemo(() => {
    if (!selectedAnalysis) return {}

    const isSameAnalysisType = settingsSnapshot?.analysisType === selectedAnalysis
    const isSameSource = sourceDatasetId === settingsSnapshot?.mainSource
    const shouldInitWithPreviousSettings = isSameAnalysisType && isSameSource

    const choosenAnalysisSettings = shouldInitWithPreviousSettings
      ? settingsSnapshot
      : { ...settings, secondSource: null }

    const selectedAnalysisItem = Catalog.find((analysis) => analysis.id === selectedAnalysis)
    if (!selectedAnalysisItem) throw new Error("Choosen analysis doesn't exist")

    return { shouldInitWithPreviousSettings, choosenAnalysisSettings, selectedAnalysisItem }
  }, [selectedAnalysis, settings, settingsSnapshot, sourceDatasetId])

  useEffect(() => {
    if (!shouldInitWithPreviousSettings) {
      dispatch(setSecondSourceTable(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, shouldInitWithPreviousSettings, selectedAnalysisItem])

  useEffect(() => {
    if (query) {
      dispatch(
        setQuerySource({
          query,
          geoColumn
        })
      )
    } else {
      dispatch(setQuerySource(undefined))
    }
  }, [dispatch, query, sourceDatasetId, geoColumn])

  const ChoosenForm = selectedAnalysisItem?.form

  return (
    ChoosenForm && <ChoosenForm settings={choosenAnalysisSettings} onChange={setSettings} onQueryChange={setQuery} />
  )
}

interface GenerateLaunchQueryProps {
  provider: ProviderType
  output: string
  query: string
  geoColumn: string
}

export function generateLaunchQuery(analysis, { provider, ...payload }: GenerateLaunchQueryProps) {
  return analysis === AnalysisType.TradeAreas
    ? generateLaunchTradeAreasQuery(provider, payload)
    : generateCreateTableFromQuery({ provider, payload })
}
